import { useEffect, useState } from "react";
import api, { formatTL } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, HardHat, Power, Pencil, Trash2, Search, Pause, Play } from "lucide-react";
import { ConfirmDialog } from "../../components/common";
import { toast } from "sonner";

export default function Technicians() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [del, setDel] = useState(null);   // {tech, force, msg}
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", commission_percent: 30, is_active: true });

  const load = async () => {
    const [t, s] = await Promise.all([api.get("/technicians"), api.get("/cash/summary")]);
    const balances = Object.fromEntries((s.data.technician_balances || []).map(b => [b.id, b]));
    setList(t.data.map(x => ({ ...x, balance: balances[x.id]?.balance || 0, debt: balances[x.id]?.debt || 0, paid: balances[x.id]?.paid || 0, commission: balances[x.id]?.commission || 0 })));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ email: "", password: "", name: "", phone: "", commission_percent: 30, is_active: true });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ email: t.email, password: "", name: t.name, phone: t.phone || "", commission_percent: t.commission_percent, is_active: t.is_active });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        const payload = { name: form.name, phone: form.phone, commission_percent: Number(form.commission_percent), is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await api.put(`/technicians/${editId}`, payload);
        toast.success("Teknisyen güncellendi");
      } else {
        if (!form.email || !form.password) { toast.error("E-posta ve şifre zorunlu"); return; }
        await api.post("/technicians", { ...form, commission_percent: Number(form.commission_percent) });
        toast.success("Teknisyen eklendi");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const toggleActive = async (t) => {
    await api.put(`/technicians/${t.id}`, { is_active: !t.is_active });
    toast.success(t.is_active ? "Pasifleştirildi" : "Aktifleştirildi");
    load();
  };

  const doDelete = async () => {
    const t = del.tech;
    try {
      await api.delete(`/technicians/${t.id}`, { params: del.force ? { force: true } : {} });
      toast.success("Usta silindi");
      setDel(null); load();
    } catch (e) {
      if (e.response?.status === 409 && !del.force) {
        setDel({ tech: t, force: true, msg: e.response.data?.detail });
      } else { toast.error(e.response?.data?.detail || "Silinemedi"); setDel(null); }
    }
  };

  const shown = list.filter(t => {
    if (statusF === "active" && !t.is_active) return false;
    if (statusF === "paused" && t.is_active) return false;
    if (q) {
      const hay = `${t.name} ${t.email} ${t.phone || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="technicians-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Personel Yönetimi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Teknisyenler</h1>
        </div>
        <Button onClick={openCreate} data-testid="new-technician-button" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" /> Yeni Teknisyen
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Usta ara (ad, e-posta, telefon)" className="pl-9" data-testid="tech-search" />
        </div>
        {[["all", "Tümü"], ["active", "Aktif"], ["paused", "Durdurulmuş"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={statusF === k ? "default" : "outline"} onClick={() => setStatusF(k)} data-testid={`tech-filter-${k}`}>{l}</Button>
        ))}
        <span className="text-xs text-slate-500 ml-1">{shown.length} / {list.length}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.length === 0 && list.length > 0 && (
          <Card className="md:col-span-2 lg:col-span-3 p-8 text-center border border-slate-200 shadow-none text-slate-500">Filtreye uyan usta yok</Card>
        )}
        {list.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3 p-12 text-center border border-slate-200 shadow-none">
            <HardHat className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">Henüz teknisyen eklenmemiş</div>
          </Card>
        )}
        {shown.map(t => (
          <Card key={t.id} className="p-5 border border-slate-200 shadow-none card-lift" data-testid={`tech-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-display font-bold">
                  {t.name?.split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.email}</div>
                </div>
              </div>
              <span className={`status-pill ${t.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{t.is_active ? "Aktif" : "Pasif"}</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-slate-50 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Borç Oranı</div>
                <div className="font-display font-bold text-slate-900">%{t.commission_percent}</div>
              </div>
              <div className="rounded-md bg-violet-50 p-2">
                <div className="text-[10px] uppercase tracking-wider text-violet-700">Cebinde</div>
                <div className="font-mono text-sm font-bold text-violet-700">{formatTL(t.commission)}</div>
              </div>
              <div className={`rounded-md p-2 ${t.balance > 0 ? "bg-amber-50" : "bg-slate-50"}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-600">Borç</div>
                <div className="font-mono text-sm font-bold text-amber-700">{formatTL(t.balance)}</div>
              </div>
            </div>
            <div className="mt-4 text-xs text-slate-500">{t.phone || "telefon yok"}</div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(t)} data-testid={`edit-tech-${t.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" /> Düzenle
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggleActive(t)} data-testid={`toggle-tech-${t.id}`}
                title={t.is_active ? "Durdur (girişi engelle)" : "Tekrar aktifleştir"}>
                {t.is_active ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}{t.is_active ? "Durdur" : "Başlat"}
              </Button>
              <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setDel({ tech: t, force: false })} data-testid={`delete-tech-${t.id}`} title="Sil">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Teknisyen Düzenle" : "Yeni Teknisyen"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input data-testid="tech-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-posta *</Label><Input type="email" disabled={!!editId} data-testid="tech-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>{editId ? "Yeni Şifre (opsiyonel)" : "Şifre *"}</Label><Input type="password" data-testid="tech-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div><Label>Şirkete Borç Oranı (%) *</Label><Input data-testid="tech-commission-input" type="number" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} /></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktif</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="save-tech-button" className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!del} onOpenChange={(v) => !v && setDel(null)}
        title={del?.force ? "Yine de silinsin mi?" : `${del?.tech?.name} silinsin mi?`}
        description={del?.force ? `${del.msg || ""} Silerseniz bu ustanın hesabı kapanır ve giriş yapamaz.` : "Usta hesabı kalıcı olarak silinir ve bir daha giriş yapamaz. Geçmiş iş kayıtları korunur. Geçici engellemek için “Durdur”u kullanın."}
        confirmText={del?.force ? "Yine de sil" : "Sil"} onConfirm={doDelete} testid="tech-delete-dialog"
      />
    </div>
  );
}
